import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';
import userMetadataApi from '../../api/userMetadataApi';
import { GLCodeContent } from '../settings/subpages/GLCode';
import { useAdminContext } from './AdminContext';

const AdminGLCodePage: React.FC = () => {
    const { selectedBusinessId, setSelectedBusinessId } = useAdminContext();

    const { data: allBusinessesData, isLoading } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
    });

    const businessOptions = useMemo(() => (
        allBusinessesData?.businesses.map((business) => ({
            id: business.business_id || business.user_id,
            name:
                business.legal_name ||
                business.dba_name ||
                business.onboarding.qbo_metadata.company_name ||
                `${business.first_name || ''} ${business.last_name || ''}`.trim() ||
                'Unnamed Business',
            subtitle: business.user_email || undefined,
        })) || []
    ), [allBusinessesData]);

    useEffect(() => {
        if (!selectedBusinessId && businessOptions.length > 0) {
            setSelectedBusinessId(businessOptions[0].id);
        }
    }, [businessOptions, selectedBusinessId, setSelectedBusinessId]);

    const selectedBusiness = businessOptions.find((option) => option.id === selectedBusinessId);

    return (
        <div className="h-full min-h-0 overflow-y-auto bg-gray-50 p-6">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Admin GL Code</h1>
                    <p className="mt-1 text-sm text-gray-500">Manage Finz and QBO account mappings for any business.</p>
                </div>
                <SearchableBusinessSelect
                    options={businessOptions}
                    value={selectedBusinessId}
                    onChange={setSelectedBusinessId}
                    placeholder={isLoading ? 'Loading businesses...' : 'Select business'}
                    emptyLabel="No businesses found"
                    widthClassName="w-[360px]"
                    showSubtitleWhenClosed={false}
                />
            </div>
            <div className="min-h-0">
                {selectedBusinessId ? (
                    <GLCodeContent
                        businessIdOverride={selectedBusinessId}
                        heading={selectedBusiness ? `GL Code: ${selectedBusiness.name}` : 'GL Code'}
                        subheading="Manage Finz accounts and inspect QBO accounts for the selected business."
                    />
                ) : (
                    <div className="rounded-xl border border-gray-200 bg-white px-6 py-10 text-sm text-gray-500">
                        Select a business to manage GL codes.
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminGLCodePage;
